import React, { useEffect, useState } from 'react'
import { SelectInput, useDataProvider, useInput } from 'react-admin'
import { CircularProgress, Box, Typography } from '@mui/material'

import CustomAudioField from '../../components/CustomAudioField'

// Props:
// - source: Firestore field to bind (e.g., introMusicPath)
// - label: UI label
// - type: music type to fetch (Intro | Outro | Chapter)
// - ...rest: any SelectInput props
const MusicMultiSelectInput = ({ source, label, type, ...rest }) => {
  const dataProvider = useDataProvider()
  const [choices, setChoices] = useState([])
  const [loading, setLoading] = useState(false)
  const [signedUrl, setSignedUrl] = useState('')
  const [signedLoading, setSignedLoading] = useState(false)

  // Subscribe to the bound value in the form
  const {
    field: { value: selectedPath },
  } = useInput({ source })

  useEffect(() => {
    let isMounted = true
    const fetchList = async () => {
      if (!type) return
      try {
        setLoading(true)
        const response = await dataProvider.create('ai-books/get-musics', {
          data: { type },
        })
        const filePaths = Array.isArray(response?.data?.filePaths)
          ? response.data.filePaths
          : []
        const mapped = filePaths.map(fp => ({
          id: fp,
          name: fp.split('/').pop() || fp,
        }))
        if (isMounted) setChoices(mapped)
      } catch (e) {
        console.error(`Error fetching musics for ${type}:`, e)
        if (isMounted) setChoices([])
      } finally {
        if (isMounted) setLoading(false)
      }
    }
    fetchList()
    return () => {
      isMounted = false
    }
  }, [type, dataProvider])

  // Fetch signed URL when a music path is selected
  useEffect(() => {
    const run = async () => {
      if (!selectedPath) {
        setSignedUrl('')
        return
      }
      try {
        setSignedLoading(true)
        const resp = await dataProvider.create('ai-books/get-signed-url', {
          data: { audioPath: selectedPath },
        })
        setSignedUrl(resp?.data?.signedUrl || '')
      } catch (e) {
        console.error('Error getting signed URL:', e)
        setSignedUrl('')
      } finally {
        setSignedLoading(false)
      }
    }
    run()
  }, [selectedPath, dataProvider])

  return (
    <Box sx={{ position: 'relative' }}>
      <SelectInput
        source={source}
        label={label}
        fullWidth
        variant="standard"
        choices={choices}
        optionText="name"
        optionValue="id"
        {...rest}
      />
      {loading && (
        <CircularProgress
          size={18}
          sx={{ position: 'absolute', top: 6, right: 6 }}
        />
      )}
      {selectedPath && (
        <Box sx={{ mt: 1 }}>
          {signedLoading ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <CircularProgress size={16} />
              <Typography variant="caption">Loading audio...</Typography>
            </Box>
          ) : signedUrl ? (
            <CustomAudioField
              label={selectedPath.split('/').pop() || selectedPath}
              signedUrl={signedUrl}
              audioPath={selectedPath}
              inline={true}
            />
          ) : (
            <Typography variant="caption" color="text.secondary">
              No audio available
            </Typography>
          )}
        </Box>
      )}
    </Box>
  )
}

export default MusicMultiSelectInput
